import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["url", "autoOpen", "autoSelect", "textFieldProps", "tooltip", "value", "onChange", "transformHttpResponse"];
//
// Copyright 2020 Emerald Associates, Inc.  All Rights Reserved.
//
// Use of this file other than by Emerald Associates, Inc. is forbidden
// unless otherwise authorized by a separate written license agreement.
//
// $Id$
//
import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";
import * as Utils from "../Utils";
var useStyles = makeStyles(function (theme) {
  return {
    textField: {
      backgroundColor: "#FBFBFB",
      minWidth: "50%",
      borderRadius: theme.shape.borderRadius
    },
    input: {
      backgroundColor: theme.palette.common.white,
      borderRadius: theme.shape.borderRadius
    },
    tag: {
      borderRadius: theme.shape.borderRadius
    },
    highlightOption: {
      color: theme.palette.primary.main
    }
  };
});
var filter = createFilterOptions();

/**
 * Creates an instance of an asynchronous dropdown list. This component will retrieve the list of options from
 *  the URL determined by the parameter {@param url}. There can be some frustration while trying to figure out
 *  what the URL should return, so here it is - is a json object with a property called "options". The options must
 *  be a list of strings that will be the options shown to the user. In Kotlin, it would look like this:
 *
 * data class AsyncBoxOptions(val options: List<String>) : Serializable
 *
 * The name of the class doesn't really matter. This is an example of the REST API method that returns an instance
 *  of the class above:
 *
 *  @GET
 *   @Path("profileList")
 *   @Produces(MediaType.APPLICATION_JSON)
 *   fun getProfileList(): ConfigurationProfilesDataSource.OptionsData {
 *
 * So there you go. This will save you frustration with possible Javascript errors.
 *
 * A few other observations:
 *
 * * Don't initialize value with null, pass an empty array or empty string.
 * * If you have returned data in a different format than the one above, you can pass a lambda
 *  in the param {@param transformHttpResponse} to change it.
 */
export default function Asynchronous(_ref) {
  var url = _ref.url,
    autoOpen = _ref.autoOpen,
    autoSelect = _ref.autoSelect,
    textFieldProps = _ref.textFieldProps,
    tooltip = _ref.tooltip,
    value = _ref.value,
    onChange = _ref.onChange,
    transformHttpResponse = _ref.transformHttpResponse,
    autocompleteProps = _objectWithoutProperties(_ref, _excluded);
  var classes = useStyles();
  var _React$useState = React.useState(true),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    mounted = _React$useState2[0],
    setMounted = _React$useState2[1];
  var focused = React.useRef(false);
  var _React$useState3 = React.useState(autoOpen && !value),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    open = _React$useState4[0],
    setOpen = _React$useState4[1];
  var _React$useState5 = React.useState([]),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    options = _React$useState6[0],
    setOptions = _React$useState6[1];
  var _React$useState7 = React.useState(false),
    _React$useState8 = _slicedToArray(_React$useState7, 2),
    loaded = _React$useState8[0],
    setLoaded = _React$useState8[1];
  var loading = open && !loaded;
  React.useEffect(function () {
    return function () {
      setMounted(false);
    };
  }, []);
  React.useEffect(function () {
    if (!loading) {
      return;
    }
    var urlString = url;
    if (typeof url === "function") {
      urlString = url();
    }
    if (!urlString) {
      setLoaded(true);
      return;
    }
    Utils.fetchData(urlString).then(function (data) {
      if (mounted) {
        if (transformHttpResponse) {
          data = transformHttpResponse(data);
        }
        var _options = data.options;
        setOptions(_options);
        if (!value && !multiple && autoSelect && _options.length > 0) {
          onChange(null, _options[0]);
        }
        if (!focused.current) {
          setOpen(false);
        }
        setLoaded(true);
      }
    }).catch(function (error) {
      setOpen(false);
      setLoaded(true);
    });
  }, [loading]);
  var defaultAutoCompleteProps = {
    getOptionLabel: function getOptionLabel(option) {
      if (option && typeof option !== "string" && option.title) {
        return /*#__PURE__*/React.createElement("div", {
          className: classes.highlightOption
        }, option.title);
      }
      return option;
    }
  };
  var multiple = autocompleteProps.multiple;
  if (multiple) {
    defaultAutoCompleteProps.renderTags = function (value, getTagProps) {
      return value.map(function (option, index) {
        return /*#__PURE__*/React.createElement(Chip, Object.assign({
          key: index,
          variant: "outlined",
          classes: {
            root: classes.tag
          },
          label: autocompleteProps && autocompleteProps.getOptionLabel ? autocompleteProps.getOptionLabel(option) : option
        }, getTagProps({
          index: index
        })));
      });
    };
  }

  // Property freeSolo is currently only supported when the list of options
  // is a list of strings. The code below adds a nice suggestion "Add <option>"
  // button whenever the user inputs unrecognized text.
  if (autocompleteProps.freeSolo) {
    var transformOption = function transformOption(option) {
      if (option && typeof option !== "string" && option.value) {
        // Create a new value from the user input
        return option.value;
      } else {
        return option;
      }
    };
    var handleChange = function handleChange(event, newValue) {
      onChange(event, transformOption(newValue));
    };
    // When accepting multiple values, the value is an array
    if (multiple) {
      handleChange = function handleChange(event, newValue) {
        onChange(event, newValue.map(function (value) {
          return transformOption(value);
        }));
      };
    }
    var defaultFreeSoloProps = {
      selectOnFocus: true,
      clearOnBlur: true,
      handleHomeEndKeys: true,
      onChange: handleChange,
      filterOptions: function filterOptions(options, params) {
        var filtered = filter(options, params);

        // Suggest the creation of a new value
        if (params.inputValue !== "") {
          filtered.push({
            title: "Add " + params.inputValue,
            value: params.inputValue
          });
        }
        return filtered;
      }
    };
    defaultAutoCompleteProps = _objectSpread(_objectSpread({}, defaultAutoCompleteProps), defaultFreeSoloProps);
  }
  autocompleteProps = _objectSpread(_objectSpread({}, defaultAutoCompleteProps), autocompleteProps);
  return /*#__PURE__*/React.createElement(Autocomplete, Object.assign({
    autoHighlight: true,
    disableClearable: true,
    onChange: onChange
  }, autocompleteProps, {
    open: open,
    onOpen: function onOpen() {
      setOpen(true);
    },
    onClose: function onClose() {
      if (!loading) {
        setOpen(false);
      }
    },
    value: value,
    onFocus: function onFocus() {
      return focused.current = true;
    },
    onBlur: function onBlur() {
      return focused.current = false;
    },
    options: options,
    loading: loading,
    renderInput: function renderInput(params) {
      var textField = /*#__PURE__*/React.createElement(TextField, Object.assign({
        className: classes.textField,
        variant: "outlined"
      }, params, textFieldProps, {
        InputProps: _objectSpread(_objectSpread(_objectSpread({
          className: classes.input
        }, params.InputProps), textFieldProps.InputProps), {}, {
          endAdornment: /*#__PURE__*/React.createElement(React.Fragment, null, loading ? /*#__PURE__*/React.createElement(CircularProgress, {
            color: "inherit",
            size: 20
          }) : null, params.InputProps.endAdornment)
        })
      }));
      if (tooltip) {
        return /*#__PURE__*/React.createElement(Tooltip, {
          style: {
            maxWidth: 500,
            width: "100%"
          },
          title: /*#__PURE__*/React.createElement("span", {
            style: {
              whiteSpace: "pre-line"
            }
          }, tooltip)
        }, textField);
      }
      return textField;
    }
  }));
}